import React, { useEffect, useState, useRef } from 'react';
import { blueColour, goldColour, lighterBlueColour } from '../pages/Home';

export default function AudioProgressBar({
  audioContextRef,
  audioBuffersRef,
  audioController,
  currentTrackId,
  timeCallback = () => {},
  ewave = true,
}) {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [wave, setWave] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [bufferedPercent, setBufferedPercent] = useState(0);

  const progressBarRef = useRef(null);
  const animationFrameRef = useRef();

  // Track the last time we called the callback
  const lastCallbackTimeRef = useRef(0);
  
  useEffect(() => {
    const bufferObject = audioBuffersRef.current.get(currentTrackId);
    const buffer = bufferObject?.buffer;
    const waveInBuffer = bufferObject?.waveform;
    if (waveInBuffer) {
      setWave(waveInBuffer);
    }
    if (buffer) {
      setDuration(buffer.duration);
    }

    const updateProgress = () => {
      if (
        !isDragging &&
        currentTrackId &&
        audioController &&
        audioContextRef.current &&
        audioController.startTimeRef?.current != null &&
        audioController.pausedAtRef?.current == null &&
        audioController.isPlaying // Only update if audio is actually playing
      ) {
        const elapsed = audioContextRef.current.currentTime - audioController.startTimeRef.current;
        const time = Math.min(elapsed, buffer?.duration || 0);
        
        // Update local state for smooth progress bar
        setCurrentTime(time);
        
        // Only call the callback once per second to avoid excessive updates
        const currentSecond = Math.floor(time);
        if (currentSecond !== lastCallbackTimeRef.current) {
          lastCallbackTimeRef.current = currentSecond;
          timeCallback({ currentTime: time, duration: buffer?.duration });
        }
      }

      animationFrameRef.current = requestAnimationFrame(updateProgress);
    };

    animationFrameRef.current = requestAnimationFrame(updateProgress);
    return () => cancelAnimationFrame(animationFrameRef.current);
  }, [audioController, currentTrackId, isDragging, timeCallback]);

  useEffect(() => {
    const bufferObject = audioBuffersRef.current.get(currentTrackId);
    const buffer = bufferObject?.buffer;
    const waveInBuffer = bufferObject?.waveform;

    if (waveInBuffer) {
      setWave(waveInBuffer);
    }
    if (buffer) {
      setBufferedPercent(100);
    }
  }, [currentTrackId, audioBuffersRef]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const seekFromClientX = (clientX) => {
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(clientX, rect.left), rect.right);
    const percent = (x - rect.left) / rect.width;
    return Math.max(0, Math.min(duration, duration * percent));
  };

  const handleSeek = (clientX) => {
    const newTime = seekFromClientX(clientX);
    audioController.seek(currentTrackId, newTime);
    setCurrentTime(newTime);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    const newTime = seekFromClientX(e.clientX);
    setCurrentTime(newTime);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    const newTime = seekFromClientX(e.clientX);
    setCurrentTime(newTime);
  };

  const handleMouseUp = (e) => {
    setIsDragging(false);
    handleSeek(e.clientX);
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    const touch = e.touches[0];
    const newTime = seekFromClientX(touch.clientX);
    setCurrentTime(newTime);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const newTime = seekFromClientX(touch.clientX);
    setCurrentTime(newTime);
  };

  const handleTouchEnd = (e) => {
    setIsDragging(false);
    const touch = e.changedTouches[0];
    handleSeek(touch.clientX);
    window.removeEventListener('touchmove', handleTouchMove);
    window.removeEventListener('touchend', handleTouchEnd);
  };

  const handleKeyDown = (e) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
    e.preventDefault();

    const delta = e.key === 'ArrowRight' ? 5 : -5;
    const newTime = Math.max(0, Math.min(duration, currentTime + delta));
    audioController.seek(currentTrackId, newTime);
    setCurrentTime(newTime);
  };

  const progressPercent = (currentTime / duration) * 100;

  return (
    <div
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        userSelect: 'none',
      }}
      aria-label="Audio progress bar"
    >
      <span style={{ fontSize: '14px', width: '40px' }}>{formatTime(currentTime)}</span>

      <div
        ref={progressBarRef}
        style={{ position: 'relative', flex: 1, height: '100px', cursor: 'pointer' }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {ewave && wave ? (
<svg width="100%" height="100%" viewBox="0 0 1000 40" preserveAspectRatio="none">
  {(() => {
    const maxAmplitude = Math.max(...wave);
    return wave.map((val, idx) => {
      const x = (idx / wave.length) * 1000;
      const normalized = maxAmplitude === 0 ? 0 : val / maxAmplitude;
      const scaledY = normalized * 20;

      const percent = (idx / wave.length) * 100;

      let color = goldColour; // Default
      if (percent <= progressPercent) {
        color = lighterBlueColour; // Played
      } else if (percent > bufferedPercent) {
        color = '#cbd5e1'; // Unbuffered (light gray)
      }

      return (
        <line
          key={idx}
          x1={x}
          x2={x}
          y1={20 - scaledY}
          y2={20 + scaledY}
          stroke={color}
          strokeWidth="1"
        />
      );
    });
  })()}
  {/* Progress line */}
  <line
    x1={(progressPercent / 100) * 1000}
    x2={(progressPercent / 100) * 1000}
    y1="0"
    y2="40"
    stroke={blueColour}
    strokeWidth="2"
  />
</svg>
        ) : (
          <>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '8px',
                backgroundColor: goldColour,
                borderRadius: '4px',
                width: `${bufferedPercent}%`,
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '8px',
                backgroundColor: lighterBlueColour,
                borderRadius: '4px',
                width: `${progressPercent}%`,
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: `${progressPercent}%`,
                width: '16px',
                height: '16px',
                backgroundColor: blueColour,
                borderRadius: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            />
          </>
        )}
      </div>

      <span style={{ fontSize: '14px', width: '40px', textAlign: 'right' }}>{formatTime(duration)}</span>
    </div>
  );
};
